<template>
  <div>
    <BubbleHeader :bubble="bubble" />
    <div class="p-6">
      <div class="mb-6">
        <h2 class="text-2xl font-bold mb-2">{{ bubble.title }}</h2>
        <p class="text-sm text-gray-600">
          Created: {{ formatDate(bubble.createdAt) }}
          <span v-if="bubble.lastModified">
            | Modified: {{ formatDate(bubble.lastModified) }}
          </span>
        </p>
      </div>
      <div class="mb-6">
        <div class="border rounded-md overflow-hidden">
          <Editor v-model="editedContent" />
        </div>
      </div>
      <button class="px-4 py-2 bg-black text-white rounded hover:bg-gray-800" @click="saveContent">
        Save
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { TextBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { useRuntimeConfig } from '#app'
import { useBubbleStore } from '@/stores/bubble'
import { useToast } from '@/components/ui/toast'
import Editor from '@/components/Editor.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

useRuntimeConfig()

const { toast } = useToast()

const bubbleStore = useBubbleStore()

const props = defineProps<{
  bubble: TextBubble
}>()

const emit = defineEmits<{
  'update:bubble': [bubble: TextBubble]
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

const formatDate = (dateString: string | Date) => {
  const date = dateString instanceof Date ? dateString : new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const editedContent = ref(props.bubble.content)

const saveContent = async () => {
  try {
    const result = await bubbleStore.updateMetaString(
      props.bubble.uuid,
      'content',
      editedContent.value
    )

    if (result.success) {
      const now = new Date()
      emit('update:bubble', {
        ...props.bubble,
        content: editedContent.value,
        lastModified: now,
      })

      toast({
        title: 'Success',
        description: 'Note updated successfully',
      })
    } else {
      throw new Error(result.error)
    }
  } catch (error) {
    logError('Error in saveContent:', error)
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      variant: 'destructive',
    })
  }
}

watch(
  () => props.bubble.content,
  (newContent) => {
    editedContent.value = newContent
  }
)
</script>
